<template>
<div class="tab-pane active" id="step3">
	<h2 class="hidden-lg">{{ $t("tab.booth_set_up") }}</h2>
	<div class="row">
		<div v-if="store.tmp.selected_booth" class="col-xs-12 col-lg-9">
			<div class="row">
				<div class="col-md-12">
					<h4>{{ $t("labels.booth_package", [store.tmp.selected_booth.displayName]) }}</h4>
					<BoothPackageScheme :booth="store.tmp.selected_booth" />
					<!--<p>Im Preis ist der Auf- und Abbau enthalten</p>-->
					<br>
					<h4>{{ $t("labels.description") }}</h4>
					<article class="text-break" v-html="displayedDescription"></article>
					<article class="text-break" v-html="displayedPowerConnection"></article>
				</div>
			</div>
			<div v-if="files.length > 0" class="row mt-3">
				<div class="col-sm-12">
					<h4>{{ $t("labels.documents") }}</h4>
				</div>
				<div v-for="file in files" :key="file.hash" class="col-sm-4 col-md-3">
					<a :href="'/storage/' + file.path" target="_blank">
						<b><i class="fa fa-file-pdf-o" aria-hidden="true"></i> {{ (inEnglish) ? file.title_en : file.title_de }}</b>
					</a>
				</div>
			</div>
			<div class="row mt-3">
				<div class="col-md-12 text-right clearfix">
					<h4>{{ $t('basket.price') }}: {{ $filters.formatMoney(price) }}</h4>
					<p class="font-weight-400"><small>(1m<sup>2</sup> = {{ $filters.formatMoney(pricePerSquareMeter) }})</small></p>
				</div>
			</div>
			<div class="row">
				<div v-if="isDisplayedBoothSelected" class="col-md-12 text-center clearfix">
					<button type="button" class="btn border-btn more-black" @click="removeBooth">
						{{ displayedRemoveButtonLabel }}
					</button>
				</div>
				<div v-else class="col-md-12 text-center clearfix">
					<button type="button" class="btn btn-primary" @click="selectBooth">
						{{ displayedSelectButtonLabel }}
					</button>
				</div>
			</div>

			<div class="row mt-3" v-if="isDisplayedBoothSelected">
				<div class="col-sm-12">
					<Instructions />
				</div>
			</div>

			<ConfiguratorBoothRefinements v-if="isDisplayedBoothSelected"
				:mandatory-selected="mandatoryRefinementSelected"
        		:mandatory-count="mandatoryRefinementCount" />
			
			<div v-if="showCarpetSelection" class="row mt-3">
				<div class="col-md-12 clearfix">
					<h4>{{ $t("labels.carpet_color_selection") }}</h4>
					<div class="color-selector" v-for="color in colors" :key="color.hex">
						<div
							class="col-md-1 color-div"
							:style="'background-color: ' + color.hex"
							:class="{'active': (basket.carpetColor && basket.carpetColor.id === color.id)}"
							@click="selectBoothColor(color)">
							<span class="pick-mark fa fa-check-square-o"></span>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div v-if="alternativeBooths && store.tmp.selected_booth != null"
			class="hidden-sm hidden-xs hidden-md col-md-3 mt-3">
			<h6>{{ $t("labels.alternative_packages") }}:</h6>
			<div class="alternative_list wow fadeInRight animated">
				<div id="list-type" class="proerty-th" v-for="booth in alternativeBooths" :key="booth.id">
					<div class="box-two proerty-item alt-booth-block" v-show="store.tmp.selected_booth != null && booth.id != store.tmp.selected_booth.id">
						<a href="#" @click="displayBoothDetails(booth)">
							<div class="item-thumb" v-if="booth.previewImage != null">
								<img :src="booth.previewImage">
							</div>
							<div class="item-entry row">
								<div class="col-md-12" :class="{'item-entry--with-price': booth.price > 0}">
									<h6>{{ booth.displayName }}</h6>
									<h6 v-if="booth.price > 0" class="text-right"><small>{{ $filters.formatMoney(booth.price) }}</small></h6>
								</div>
								<div class="col-md-12 text-right font-weight-400">
									(1m<sup>2</sup> = {{ $filters.formatMoney(booth.pricePerSquareMeter) }})
								</div>
							</div>
						</a>
					</div>
				</div>
			</div>
		</div>
	</div>
	<div class="row" v-if="!isExhibitionClosed">
		<div class="col-md-12">
			<hr>
			<div class="wizard-footer">
				<div  class="pull-right">
					<a v-show="mandatoryRefinementSelected" href="#">
						<input
							type='button'
							class='btn btn-next btn-warning'
							name='next'
							:value='($t("tab.articles") + " " + $t("buttons.next_to"))'
							@click="store.updateCurrentTab('articles')" />
					</a>
					<button v-if="!mandatoryRefinementSelected"
						type="button"
						class="btn btn-warning"
						@click="showNotice">
						{{ $t("tab.articles") + " " + $t("buttons.next_to") }}
					</button>
				</div>
				<div class="pull-left">
						<input
							type='button'
							class='btn btn-previous btn-warning'
							name='previous'
							:value='($t("buttons.prev_to") + " " + $t("tab.booth_selection"))'
							@click="store.updateCurrentTab('booths')" />
				</div>
				<div class="clearfix"></div>
			</div>
		</div>
	</div>
</div>
</template>

<script setup>
import { onMounted, ref, computed, watch } from 'vue'
import { useMainStore } from '@/store'
import { useI18n } from 'vue-i18n'
import Swal from 'sweetalert2'
import { addLateDeliverySurcharge } from '@/utils/helper'
import { filterAvailableRefinements } from '@/utils/boothRefinements'
import { hideLoadingIndicator, showLoadingIndicator } from '@/utils/toast'
import BoothPackageScheme from '@/components_new/Configurator/BoothPackageScheme.vue'
import Instructions from '@/components_new/Configurator/Instructions.vue'

const { t } = useI18n({ useScope: 'global' })
const store = useMainStore()
// define passed props
const props = defineProps({
	basket: {
		type: Object,
		required: true,
	},
	colors: {
		type: Array,
		default: [],
	},
	alternativeBooths: {
		type: Array,
		default: [],
	},
	exhibition: {
		type: Object,
		required: true,
	},
	views: {
		type: Object,
		required: true,
	},
	isExhibitionClosed: {
		type: Boolean,
		default: false,
	},
})
// define emits
const emit = defineEmits(['unselectBooth', 'saveBasket'])
// define reactive props
const currentImage = ref(0)
const boothLabel = ref('')
// define computed props
const locale = computed(() => store.settings.locale)
const shoppingCart = computed(() => store.settings.shopping_cart)
const basket = computed(() => store.settings.basket)
const earlyBirdMode = computed(() => store.tmp.early_bird_mode)
const lateDelivery = computed(() => store.tmp.late_delivery)
const disabledBoothRefinementArticleIds = computed(() => store.availability.disabled_booth_refinement_ids)
const bookableArticleIds = computed(() => store.availability.bookable_article_ids)
const inEnglish = computed(() => store.inEnglish)

const isBoothDisplayed = computed(() => {
	return (store.tmp.selected_booth !== null)
})

const isDisplayedBoothSelected = computed(() => {
	if (isBoothDisplayed.value && props.basket.selectedBooth != null) {
		return (props.basket.selectedBooth.id == store.tmp.selected_booth.id)
	}

	return false
})

const displayedDescription = computed(() => {
	if (!isBoothDisplayed.value) {
		return ""
	}
	const description = (inEnglish.value)
		? store.tmp.selected_booth.description_en
		: store.tmp.selected_booth.description

	return description.replace(/(?:\r\n|\r|\n)/g, '<br />')
})

const displayedPowerConnection = computed(() => {
	if (!isBoothDisplayed.value) {
		return ""
	}

	const description = (inEnglish.value)
		? store.tmp.selected_booth.power_connection_en
		: store.tmp.selected_booth.power_connection
	if (description === null) {
		return ""
	}

	return description.replace(/(?:\r\n|\r|\n)/g, '<br />')
})

const displayedSelectButtonLabel = computed(() => {
	if (!isBoothDisplayed.value) {
		return ""
	}
	const displayName = (inEnglish.value)
		? store.tmp.selected_booth.name_en
		: store.tmp.selected_booth.name
	const buttonLabel = (inEnglish.value)
		? t('buttons.select') + " " + t('basket.booth_package') + " " + displayName
		: t('basket.booth_package') + " " + displayName + " " + t('buttons.select')
	
	return buttonLabel
})

const displayedRemoveButtonLabel = computed(() => {
	if (!isBoothDisplayed.value) {
		return ""
	}

	const displayName = (inEnglish.value)
		? store.tmp.selected_booth.name_en
		: store.tmp.selected_booth.name
	const buttonLabel = t('buttons.remove_booth').replace("[packageName]", displayName)

	return buttonLabel
})

const showCarpetSelection = computed(() => {
	return props.basket.selectedBooth
		&& isDisplayedBoothSelected.value
		&& props.exhibition.booth_carpet_enabled
})

const files = computed(() => {
	if (
		!isBoothDisplayed.value
		|| props.views === null
		|| props.views.boothSetup === null
		|| store.tmp.selected_booth === null
	) {
		return []
	}

	if (
		store.tmp.selected_booth.files === undefined
		|| store.tmp.selected_booth.files === null
	) {
		return []
	}

	return Object.values(store.tmp.selected_booth.files)
})

const mandatoryRefinementSelected = computed(() => {

	const count = shoppingCart.value.booth_configuration.reduce((a, configuration) => {
		if (configuration.booth_refinement.mandatory) {
			a++
		}

		return a
	}, 0)

  const target = (basket.value && basket.value.selectedBooth)
		? basket.value.selectedBooth.refinements.reduce((t, refinement) => {
			if (
				refinement.mandatory
				&& basket.value.boothType !== null
				&& refinement.booth_type === basket.value.boothType.type
			) {
				t++
			}
			return t
		}, 0)
		: 1

	return count === target || availableRefinements.value.length === 0
})

const mandatoryRefinementCount = computed(() => {

  return basket.value.selectedBooth ? basket.value.selectedBooth.refinements.reduce((t, refinement) => {
    if (refinement.mandatory && refinement.booth_type === basket.value.boothType.type) {
      t++
    }
    return t
  }, 0) : 1
})

const mandatoryRefinementCountSelected = computed(() => {

  return shoppingCart.value.booth_configuration.reduce((a, configuration) => {
    if (configuration.booth_refinement.mandatory && configuration.booth_refinement.booth_type === basket.value.boothType.type) {
      a++
    }

    return a
  }, 0)
})

const price = computed(() => {
	return (earlyBirdMode.value)
		? addLateDeliverySurcharge(store.tmp.selected_booth.price, lateDelivery.value)
		: store.tmp.selected_booth.price
})

const pricePerSquareMeter = computed(() => {
	return (earlyBirdMode.value)
		? addLateDeliverySurcharge(store.tmp.selected_booth.pricePerSquareMeter, lateDelivery.value)
		: store.tmp.selected_booth.pricePerSquareMeter
})

const availableRefinements = computed(() => {
	return filterAvailableRefinements(
		shoppingCart.value,
		disabledBoothRefinementArticleIds.value,
		false,
		bookableArticleIds.value
	)
})
// define watchers
watch(() => boothLabel.value, () => {
	saveBoothLabel()
})
// define methods
const displayBoothDetails = (booth) => {
	if (booth.minimum_square_meter <= shoppingCart.value.getSquareMeter()) {
		store.tmp.selected_booth = booth
	} else {
		Swal.fire({
			title: t('configurator.notices.titles.booth_package_selection'),
			html: t('configurator.notices.messages.minimum_square_meter').replace("[SQUARE_METER]", booth.minimum_square_meter),
			showCancelButton: false,
			confirmButtonText: t('configurator.buttons.ok'),
		})
	}
}

const nextPrevImage = (booth) => {
	currentImage.value = ((currentImage.value + 1) < props.basket.selectedBooth.previewImages.length)
		? currentImage.value + 1
		: 0
}

const prevPrevImage = (booth) => {
	currentImage.value = ((currentImage.value - 1) >= 0)
		? currentImage.value - 1
		: props.basket.selectedBooth.previewImages.length - 1
}

const removeBooth = () => {
	shoppingCart.value.removeBooth()
	store.tmp.booth_package_documents = []
	emit('unselectBooth')
}

const selectBooth = async () => {
	showLoadingIndicator(
		t('configurator.indicators.check_availability'),
		t('configurator.indicators.one_moment_please')
	)
	await store.fetchAvailability()
		.finally(() => {
			setTimeout(() => {
				hideLoadingIndicator()
			}, 500)
		})
	props.basket.selectedBooth = store.tmp.selected_booth
	boothLabel.value = props.basket.label
	shoppingCart.value.selectBooth(_.cloneDeep(store.tmp.selected_booth))
	if (store.tmp.selected_booth.files) {
		store.tmp.booth_package_documents = Object.values(_.cloneDeep(store.tmp.selected_booth.files))
	}
	emit('saveBasket')
}

const selectBoothColor = (color) => {
	store.settings.basket.carpetColor = color
	emit('saveBasket')
}

const saveBoothLabel = _.debounce(() => {
		props.basket.label = boothLabel.value
		emit('saveBasket')
	},
	500
)

const showNotice = () => {
	Swal.fire({
    title: t('configurator.notices.titles.lettering'),
    html: t('configurator.notices.messages.lettering') + (mandatoryRefinementCount.value > 1 ? '<br><br>(' + mandatoryRefinementCountSelected.value + ' / ' + mandatoryRefinementCount.value + ') ' + t('configurator.notices.messages.lettering_selected'): '') ,
    showCancelButton: false,
    confirmButtonText: t('configurator.buttons.ok'),
  })
}

onMounted(() => {
	boothLabel.value = props.basket.label
})
</script>
